import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { type PaginatedResponse } from '../../../api/client'
import { type PaginationOptions } from '../../../api/pagination'
import { fetchAddress, fetchAddresses, fetchAllAddresses } from '../api/addressApi'
import { type Address } from '../../../utils/types'
import { addressKeys } from '../utils/queryKeys'
import { getPKfromSelf } from '../../../utils/functions'

/**
 * Hook to fetch paginated addresses.
 */
export function useAddresses(
  options: PaginationOptions = {},
  enabled = true
): UseQueryResult<PaginatedResponse<Address>, Error> {
  return useQuery({
    queryKey: addressKeys.list({
      page: options.page,
      pageSize: options.pageSize,
      filters: options.filters,
    }),
    queryFn: () => fetchAddresses(options),
    enabled,
  })
}

/**
 * Hook to fetch a single address by ID or self URL.
 */
export function useAddress(
  idOrUrl: string | number | undefined,
  enabled = true
): UseQueryResult<Address, Error> {
  return useQuery({
    queryKey: addressKeys.detail(typeof idOrUrl === 'number' ? idOrUrl : Number(getPKfromSelf(String(idOrUrl)))),
    queryFn: () => {
      if (idOrUrl === undefined || idOrUrl === null) {
        throw new Error('Address ID or URL is required')
      }
      return fetchAddress(idOrUrl)
    },
    enabled: enabled && idOrUrl !== undefined && idOrUrl !== null,
  })
}

/**
 * Hook to fetch all addresses (recursively requests every page).
 */
export function useAllAddresses(
  options: Omit<PaginationOptions, 'page'> = {},
  enabled = true
): UseQueryResult<Address[], Error> {
  return useQuery({
    queryKey: [...addressKeys.all, 'all', options] as const,
    queryFn: () => fetchAllAddresses(options),
    enabled,
  })
}
